/**
 * airlineLookup — best-effort airline name from a flight callsign.
 *
 * ADS-B feeds (airplanes.live, ADS-B Exchange, OpenSky) carry the
 * callsign — the first three characters are an ICAO airline designator
 * for commercial flights (e.g. UAL1234 = United Airlines flight 1234).
 * General-aviation aircraft typically broadcast registration as the
 * callsign (e.g. N12345); we return null for those.
 *
 * Note: this is a static lookup, not an API call. We don't know the
 * origin/destination of a flight from ADS-B alone — that requires a
 * paid integration (FlightAware AeroAPI, FR24 Business). The label
 * shows what we can learn locally; richer flight info is a future
 * upgrade path gated on a vendor decision.
 *
 * Coverage focuses on the largest North-American + international
 * carriers a New England-based deployment is likely to see overhead.
 * Add to the table as you encounter new prefixes — incomplete coverage
 * just falls through to null (callsign still rendered raw).
 */

const AIRLINE_BY_ICAO: Record<string, string> = {
  // United States — majors
  AAL: 'American Airlines',
  DAL: 'Delta Air Lines',
  UAL: 'United Airlines',
  SWA: 'Southwest Airlines',
  // United States — low-cost / regional
  JBU: 'JetBlue',
  ASA: 'Alaska Airlines',
  FFT: 'Frontier Airlines',
  NKS: 'Spirit Airlines',
  ALK: 'Alaska Airlines',
  HAL: 'Hawaiian Airlines',
  SCX: 'Sun Country',
  ENY: 'Envoy Air',
  RPA: 'Republic Airways',
  SKW: 'SkyWest Airlines',
  AAY: 'Allegiant Air',
  // United States — cargo
  FDX: 'FedEx Express',
  UPS: 'UPS Airlines',
  ABX: 'ABX Air',
  GTI: 'Atlas Air',
  ATN: 'Air Transport International',
  // Canada
  ACA: 'Air Canada',
  WJA: 'WestJet',
  POE: 'Porter Airlines',
  TSC: 'Air Transat',
  JZA: 'Jazz Aviation',
  // Mexico / Central / South America
  AMX: 'Aeromexico',
  VOI: 'Volaris',
  AVA: 'Avianca',
  LAN: 'LATAM',
  TAM: 'LATAM Brasil',
  AAR: 'Asiana Airlines',
  // Western Europe
  BAW: 'British Airways',
  VIR: 'Virgin Atlantic',
  EZY: 'easyJet',
  RYR: 'Ryanair',
  IBE: 'Iberia',
  AFR: 'Air France',
  AFL: 'Aeroflot',
  KLM: 'KLM',
  DLH: 'Lufthansa',
  CFG: 'Condor',
  AUA: 'Austrian Airlines',
  SWR: 'Swiss',
  TAP: 'TAP Portugal',
  AEE: 'Aegean Airlines',
  // Northern Europe
  SAS: 'SAS',
  FIN: 'Finnair',
  NOZ: 'Norwegian',
  ICE: 'Icelandair',
  // Middle East
  ETD: 'Etihad Airways',
  UAE: 'Emirates',
  QTR: 'Qatar Airways',
  SVA: 'Saudia',
  ELY: 'El Al',
  // Asia / Pacific
  ANA: 'All Nippon',
  JAL: 'Japan Airlines',
  KAL: 'Korean Air',
  CES: 'China Eastern',
  CSN: 'China Southern',
  CCA: 'Air China',
  CPA: 'Cathay Pacific',
  SIA: 'Singapore Airlines',
  THA: 'Thai Airways',
  QFA: 'Qantas',
  ANZ: 'Air New Zealand',
  // Charters / fractional / regional often seen in New England
  XOJ: 'JSX',
  EJA: 'NetJets',
  FLG: 'Endeavor Air',
  GJS: 'GoJet Airlines',
  PDT: 'Piedmont Airlines',
  // US military (commonly visible on ADS-B)
  RCH: 'USAF Reach',
  AMC: 'USAF AMC',
  UAVN: 'US Army',
  CNV: 'US Navy',
  CGD: 'US Coast Guard',
};

/**
 * Decode an airline name from a callsign. Returns null when:
 *   - Callsign is missing
 *   - Callsign looks like a registration (starts with N + digits, etc.)
 *   - The 3-letter prefix isn't in the lookup table
 */
export function airlineFromCallsign(callsign: string | null): string | null {
  if (!callsign) return null;
  const trimmed = callsign.trim().toUpperCase();
  if (trimmed.length < 4) return null;
  // Heuristic for tail-number callsigns: N + digits, or 1-2 letters + digits
  // (registration plates from various countries). These don't carry an
  // airline prefix — they're general-aviation aircraft.
  if (/^[A-Z]{1,2}\d/.test(trimmed)) return null;
  const prefix = trimmed.slice(0, 3);
  return AIRLINE_BY_ICAO[prefix] ?? null;
}
